import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { SKETCH_PATTERNS } from '../lib/images';
import { getIQQuestions, getIQResult, submitIQAnswers, getEntitlements } from '../lib/api';
import useRazorpay from '../hooks/useRazorpay';
import InlineContactFields from '../components/purchase/InlineContactFields';
import { captureContact } from '../lib/api';
import useContactAutoCapture from '../hooks/useContactAutoCapture';
import useCurrency from '../hooks/useCurrency';
import usePromoPreview from '../hooks/usePromoPreview';
import PriceWithPromo from '../components/PriceWithPromo';
import { trackEvent } from '../lib/firebase';
import { fbTrack } from '../lib/fbpixel';
import { downloadElementAsPdf } from '../lib/pdf';
import {
  Brain, Zap, Target, Clock, ChevronRight, Loader2, Lock,
  Crown, Share2, RefreshCw, BarChart3, TrendingUp, Sparkles,
  ArrowRight, CheckCircle2, XCircle, Timer, Download, FileText,
  Volume2, Wifi, BatteryCharging, CheckCircle
} from 'lucide-react';

export default function IQTestPage() {
  const { deviceId, user } = useAuth();
  const [step, setStep] = useState('loading'); // loading | intro | prep | test | result
  const [result, setResult] = useState(null);
  const [bank, setBank] = useState(null); // { questions, categories }
  const [bankError, setBankError] = useState('');
  const [entitled, setEntitled] = useState(false);  // has user paid for IQ report (or premium)

  useEffect(() => {
    async function check() {
      try {
        const [resRes, entRes] = await Promise.allSettled([
          getIQResult(deviceId),
          getEntitlements(deviceId, user?.email),
        ]);
        if (entRes.status === 'fulfilled') {
          const e = entRes.value.data || {};
          setEntitled(Boolean(e.premium || e.iq_report));
        }
        if (resRes.status === 'fulfilled' && resRes.value.data?.has_result) {
          setResult(resRes.value.data);
          setStep('result');
          return;
        }
      } catch {}
      setStep('intro');
    }
    check();
  }, [deviceId]);

  // Load question bank once (needed both for test and result category labels)
  useEffect(() => {
    async function loadBank() {
      try {
        const res = await getIQQuestions();
        setBank(res.data);
      } catch {
        setBankError('Could not load test questions. Please try again.');
      }
    }
    loadBank();
  }, []);

  const handleComplete = async (answers) => {
    try {
      const res = await submitIQAnswers({ device_id: deviceId, answers });
      const scored = res.data?.result || {};
      setResult({ ...scored, completed_at: new Date().toISOString() });
      trackEvent('iq_test_complete', {
        cognitive_score: scored.cognitiveScore,
        iq_score: scored.iqScore,
        iq_label: scored.iqLabel,
        accuracy: scored.accuracy,
        total_time: scored.totalTime,
        total_questions: scored.totalQuestions,
        // GA4 conversion properties (potential paywall unlock @ ₹499)
        value: 499,
        currency: 'INR',
      });
      // Meta Pixel — Lead (high-intent pre-purchase signal)
      fbTrack('Lead', {
        value: 499,
        currency: 'INR',
        content_name: 'IQ Test Complete',
        content_category: 'iq_report',
      });
    } catch {
      setResult({ answers, cognitiveScore: 0, iqScore: 70, iqLabel: 'Unavailable', totalCorrect: 0, totalQuestions: answers.length, totalTime: 0, avgTime: 0, categoryScores: {}, accuracy: 0 });
    }
    setStep('result');
  };

  if (step === 'loading') return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--primary)' }} />
    </div>
  );

  return (
    <div data-testid="iq-test-page">
      {step === 'intro' && <IQIntro onStart={() => { trackEvent('iq_test_start'); setStep('prep'); }} />}
      {step === 'prep' && <IQPrep onContinue={() => setStep('test')} onBack={() => setStep('intro')} />}
      {step === 'test' && (
        bank ? <IQTestFlow onComplete={handleComplete} questions={bank.questions} categories={bank.categories} /> :
        <div className="flex flex-col items-center justify-center py-24 gap-3">
          <Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--primary)' }} />
          {bankError && <p className="text-sm" style={{ color: '#DC2626' }}>{bankError}</p>}
        </div>
      )}
      {step === 'result' && <IQResult result={result} deviceId={deviceId} user={user} categories={bank?.categories || {}} preUnlocked={entitled} onRetake={() => { setResult(null); setStep('intro'); }} />}
    </div>
  );
}

// ─── INTRO ───
const INTRO_CATEGORIES = [
  { id: 'pattern', name: 'Pattern Recognition', icon: '🔷' },
  { id: 'logic',   name: 'Logical Reasoning',   icon: '🧩' },
  { id: 'memory',  name: 'Memory',              icon: '🧠' },
  { id: 'focus',   name: 'Focus & Speed',       icon: '🎯' },
];

function IQIntro({ onStart }) {
  return (
    <div className="animate-fade-in max-w-2xl mx-auto text-center">
      <div className="rounded-2xl overflow-hidden mb-8 relative py-16 px-8 keep-light-theme"
        style={{ background: 'linear-gradient(135deg, #F2EFE9 0%, #E5E1D8 50%, #D4CBB3 100%)' }}>
        <div className="absolute inset-0 opacity-10"
          style={{ backgroundImage: `url("${SKETCH_PATTERNS.zenStones}")`, backgroundSize: '250px', backgroundPosition: 'center', backgroundRepeat: 'no-repeat' }} />
        <div className="relative z-10">
          <div className="w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center"
            style={{ background: '#3B82F6', color: 'white' }}>
            <Zap className="w-10 h-10" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold mb-3" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>
            Test your IQ, focus, and thinking in 3 minutes
          </h1>
          <p className="text-sm leading-relaxed max-w-md mx-auto" style={{ color: 'var(--muted-fg)' }}>
            Get an estimate of your Intelligence Quotient and cognitive strengths across pattern recognition, logic, memory, and focus.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
        {INTRO_CATEGORIES.map((cat, i) => (
          <div key={cat.id} className={`rounded-lg p-4 border animate-fade-in stagger-${i + 1}`}
            style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
            <span className="text-2xl block mb-2">{cat.icon}</span>
            <p className="font-bold text-xs" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>{cat.name}</p>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-center gap-6 mb-8 text-xs" style={{ color: 'var(--muted-fg)' }}>
        <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> 3-5 min</span>
        <span className="flex items-center gap-1"><Target className="w-3.5 h-3.5" /> 20 questions</span>
        <span className="flex items-center gap-1"><Brain className="w-3.5 h-3.5" /> IQ score</span>
      </div>

      <button onClick={onStart} data-testid="start-iq-test"
        className="px-10 py-3.5 rounded-lg font-semibold transition-all hover:opacity-90 active:scale-[0.98] inline-flex items-center gap-2"
        style={{ background: '#3B82F6', color: 'white' }}>
        Get Ready <ArrowRight className="w-5 h-5" />
      </button>
    </div>
  );
}

// ─── PREP ───
const PREP_ITEMS = [
  { Icon: Volume2,         title: 'Find a quiet spot',       sub: 'No conversations or background chatter for ~5 minutes.' },
  { Icon: Wifi,             title: 'Close other tabs & apps', sub: 'Notifications will break your focus mid-question.' },
  { Icon: BatteryCharging,  title: 'Stable device & internet',sub: 'Questions are timed — a disconnect counts as a miss.' },
  { Icon: Timer,            title: 'Each question is timed',  sub: '15 to 50 seconds per question. Work quickly but carefully.' },
  { Icon: Brain,            title: 'Memory questions',        sub: 'You\'ll briefly see the content to memorize, then answer.' },
  { Icon: CheckCircle,      title: 'Answer in one sitting',   sub: 'Progress isn\'t saved mid-test. Finish the whole flow.' },
];

function IQPrep({ onContinue, onBack }) {
  const [acknowledged, setAcknowledged] = useState(false);
  return (
    <div className="animate-fade-in max-w-2xl mx-auto" data-testid="iq-prep">
      <button onClick={onBack} className="flex items-center gap-1 text-sm mb-4" style={{ color: '#3B82F6' }}
        data-testid="iq-prep-back">
        <ChevronRight className="w-4 h-4 rotate-180" /> Back
      </button>

      <div className="rounded-2xl p-6 sm:p-8 border mb-5"
        style={{ background: 'linear-gradient(135deg, #3B82F608 0%, #8B5CF608 100%)', borderColor: '#3B82F630' }}>
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="w-4 h-4" style={{ color: '#3B82F6' }} />
          <p className="text-xs font-semibold uppercase tracking-widest" style={{ color: '#3B82F6', fontFamily: 'Manrope' }}>
            Before you begin
          </p>
        </div>
        <h2 className="text-2xl sm:text-3xl font-bold mb-2" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>
          Let's set you up to do your best
        </h2>
        <p className="text-sm leading-relaxed" style={{ color: 'var(--muted-fg)' }}>
          A clean environment makes the difference between an average and an accurate cognitive reading. Take a minute to prepare.
        </p>
      </div>

      <div className="space-y-3 mb-6">
        {PREP_ITEMS.map(({ Icon, title, sub }, i) => (
          <div key={i} className="rounded-lg p-4 border flex items-start gap-3 animate-fade-in"
            style={{ background: 'var(--surface)', borderColor: 'var(--border)', animationDelay: `${i * 40}ms` }}
            data-testid={`iq-prep-item-${i}`}>
            <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
              style={{ background: '#3B82F615', color: '#3B82F6' }}>
              <Icon className="w-4.5 h-4.5" />
            </div>
            <div className="flex-1">
              <p className="font-semibold text-sm" style={{ color: 'var(--text)', fontFamily: 'Manrope' }}>{title}</p>
              <p className="text-xs mt-0.5" style={{ color: 'var(--muted-fg)' }}>{sub}</p>
            </div>
          </div>
        ))}
      </div>

      <label className="flex items-center gap-2.5 cursor-pointer mb-4 select-none" data-testid="iq-prep-ack">
        <input type="checkbox" checked={acknowledged} onChange={e => setAcknowledged(e.target.checked)}
          className="w-4 h-4 rounded cursor-pointer" style={{ accentColor: '#3B82F6' }} />
        <span className="text-sm" style={{ color: 'var(--text)' }}>
          I'm in a focused environment and ready to begin.
        </span>
      </label>

      <button onClick={onContinue} disabled={!acknowledged} data-testid="iq-prep-start"
        className="w-full py-3.5 rounded-lg font-semibold transition-all hover:opacity-90 active:scale-[0.98] disabled:opacity-40 flex items-center justify-center gap-2"
        style={{ background: '#3B82F6', color: 'white' }}>
        Start the test <ArrowRight className="w-5 h-5" />
      </button>
    </div>
  );
}

// ─── TEST FLOW ───
function IQTestFlow({ onComplete, questions, categories }) {
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [timeLeft, setTimeLeft] = useState(0);
  const [questionStart, setQuestionStart] = useState(0);
  const [memoryPhase, setMemoryPhase] = useState(false);
  const [memoryCountdown, setMemoryCountdown] = useState(0);
  const timerRef = useRef(null);

  const question = questions[current];
  const total = questions.length;
  const progress = ((current) / total) * 100;
  const cat = categories[question.category] || { name: question.category, color: '#879B7D', icon: '•' };

  const handleAutoNext = useCallback(() => {
    const timeTaken = (Date.now() - questionStart) / 1000;
    const answer = {
      questionId: question.id,
      category: question.category,
      answerId: null,
      timeTaken: Math.min(timeTaken, question.timeLimit),
      timeLimit: question.timeLimit,
      timedOut: true,
    };
    const newAnswers = [...answers, answer];
    if (current < total - 1) {
      setAnswers(newAnswers);
      setCurrent(current + 1);
      setSelected(null);
    } else {
      onComplete(newAnswers);
    }
  }, [answers, current, question, questionStart, total, onComplete]);

  // Start timer for each question
  useEffect(() => {
    if (memoryPhase) return;
    setTimeLeft(question.timeLimit);
    setQuestionStart(Date.now());
    timerRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          handleAutoNext();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [current, memoryPhase, question.timeLimit, handleAutoNext]);

  // Memory phase countdown
  useEffect(() => {
    if (!question.memoryPhase || !memoryPhase) return;
    setMemoryCountdown(question.memoryTime);
    const interval = setInterval(() => {
      setMemoryCountdown(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          setMemoryPhase(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [memoryPhase, current, question.memoryPhase, question.memoryTime]);

  // Start memory phase for memory questions
  useEffect(() => {
    if (question.memoryPhase) {
      setMemoryPhase(true);
    } else {
      setMemoryPhase(false);
    }
  }, [current, question.memoryPhase]);

  const handleSelect = (optionId) => {
    if (memoryPhase) return;
    setSelected(optionId);
  };

  const handleNext = () => {
    if (!selected) return;
    clearInterval(timerRef.current);
    const timeTaken = (Date.now() - questionStart) / 1000;
    const answer = {
      questionId: question.id,
      category: question.category,
      answerId: selected,
      timeTaken: Math.min(timeTaken, question.timeLimit),
      timeLimit: question.timeLimit,
      timedOut: false,
    };
    const newAnswers = [...answers, answer];
    if (current < total - 1) {
      setAnswers(newAnswers);
      setCurrent(current + 1);
      setSelected(null);
    } else {
      onComplete(newAnswers);
    }
  };

  const timePct = (timeLeft / question.timeLimit) * 100;
  const isUrgent = timeLeft <= 5;

  return (
    <div className="max-w-2xl mx-auto" data-testid="iq-test-flow">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-sm">{cat.icon}</span>
          <span className="text-xs font-medium" style={{ color: cat.color }}>{cat.name}</span>
        </div>
        <span className="text-xs font-medium" style={{ color: 'var(--muted-fg)', fontFamily: 'Manrope' }}>
          {current + 1} / {total}
        </span>
      </div>

      {/* Progress */}
      <div className="h-1.5 rounded-full mb-2 overflow-hidden" style={{ background: 'var(--muted)' }}>
        <div className="h-full rounded-full transition-all duration-300" style={{ width: `${progress}%`, background: cat.color }} />
      </div>

      {/* Timer */}
      <div className="flex items-center justify-end gap-1.5 mb-6">
        <Timer className="w-3.5 h-3.5" style={{ color: isUrgent ? '#EF4444' : 'var(--muted-fg)' }} />
        <div className="w-24 h-1 rounded-full overflow-hidden" style={{ background: 'var(--muted)' }}>
          <div className="h-full rounded-full transition-all duration-1000"
            style={{ width: `${timePct}%`, background: isUrgent ? '#EF4444' : cat.color }} />
        </div>
        <span className="text-xs font-mono w-6 text-right" style={{ color: isUrgent ? '#EF4444' : 'var(--muted-fg)' }}>{timeLeft}s</span>
      </div>

      {/* Memory Phase */}
      {memoryPhase && (
        <div className="rounded-xl p-8 border mb-6 text-center animate-fade-in"
          style={{ background: `${cat.color}08`, borderColor: `${cat.color}30` }}>
          <p className="text-xs uppercase tracking-widest mb-4 font-semibold" style={{ color: cat.color }}>Memorize this</p>
          <p className="text-2xl sm:text-3xl font-bold tracking-wider mb-4" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>
            {question.visual}
          </p>
          <div className="w-12 h-12 rounded-full mx-auto flex items-center justify-center text-lg font-bold"
            style={{ background: `${cat.color}20`, color: cat.color }}>
            {memoryCountdown}
          </div>
        </div>
      )}

      {/* Question */}
      {!memoryPhase && (
        <div className="animate-fade-in" key={current}>
          <h2 className="text-lg sm:text-xl font-bold mb-3 leading-snug" style={{ fontFamily: 'Manrope', color: 'var(--text)' }} data-testid="iq-question-text">
            {question.text}
          </h2>

          {question.visual && !question.memoryPhase && (
            <div className="rounded-lg p-4 mb-5 text-center" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
              <p className="text-xl font-bold tracking-wider" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>{question.visual}</p>
            </div>
          )}

          {/* Options */}
          <div className="space-y-2.5">
            {question.options.map((opt, i) => {
              const isSelected = selected === opt.id;
              return (
                <button key={opt.id} onClick={() => handleSelect(opt.id)}
                  className="w-full p-4 rounded-lg border text-left transition-all hover:-translate-y-0.5"
                  style={{
                    background: isSelected ? cat.color : 'var(--surface)',
                    color: isSelected ? 'white' : 'var(--text)',
                    borderColor: isSelected ? cat.color : 'var(--border)',
                  }}
                  data-testid={`iq-option-${opt.id}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-7 h-7 rounded-full border-2 flex items-center justify-center shrink-0 text-xs font-bold"
                      style={{
                        borderColor: isSelected ? 'rgba(255,255,255,0.5)' : 'var(--border)',
                        background: isSelected ? 'rgba(255,255,255,0.2)' : 'transparent',
                        color: isSelected ? 'white' : 'var(--muted-fg)',
                      }}>
                      {String.fromCharCode(65 + i)}
                    </div>
                    <span className="text-sm font-medium">{opt.text}</span>
                  </div>
                </button>
              );
            })}
          </div>

          <button onClick={handleNext} disabled={!selected} data-testid="iq-next"
            className="mt-6 w-full py-3 rounded-lg font-semibold transition-all hover:opacity-90 active:scale-[0.98] disabled:opacity-40 flex items-center justify-center gap-2"
            style={{ background: cat.color, color: 'white' }}>
            {current < total - 1 ? 'Next' : 'See Results'}
          </button>
        </div>
      )}
    </div>
  );
}

// ─── RESULT ───
function IQResult({ result, deviceId, user, onRetake, categories = {}, preUnlocked = false }) {
  const { pay, loading: payLoading, error: payError, authDialog } = useRazorpay();
  const { setUserDirect } = useAuth();
  const [unlocked, setUnlocked] = useState(preUnlocked);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [contactEmail, setContactEmail] = useState(user?.email || '');
  const [contactPhone, setContactPhone] = useState('');
  const [contactError, setContactError] = useState('');
  // Two-step paywall: contact → reveal price.
  // Price + Unlock CTA stay hidden until the user submits their email; on
  // submit we save to the DB synchronously, then reveal pricing.
  const [revealedPrice, setRevealedPrice] = useState(!!user?.email);
  const [revealing, setRevealing] = useState(false);
  const reportRef = useRef(null);
  const currency = useCurrency();
  const promo = usePromoPreview();
  useEffect(() => {
    if (preUnlocked) setUnlocked(true);
  }, [preUnlocked]);

  const isLocked = !unlocked;
  const price = 499;
  const emailIsValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((contactEmail || '').trim());

  // Robust auto-capture: fires on mount (already-logged-in users), on email
  // blur (when valid), and as a sendBeacon on tab close. Backstops the case
  // where a user types a valid email but never clicks "Unlock".
  const autoCapture = useContactAutoCapture({
    deviceId,
    email: contactEmail,
    phone: contactPhone,
    source: 'iq',
  });

  // Step 1 — save email/phone to DB, then reveal the price.
  const handleRevealPrice = async () => {
    const trimmedEmail = (contactEmail || '').trim().toLowerCase();
    const trimmedPhone = (contactPhone || '').trim();
    if (!emailIsValid) {
      setContactError('Please enter a valid email.');
      return;
    }
    setContactError('');
    setRevealing(true);
    try {
      await captureContact({
        device_id: deviceId,
        email: trimmedEmail,
        phone: trimmedPhone || null,
        source: 'iq',
      });
      try {
        setUserDirect({ ...(user || {}), email: trimmedEmail, guest: user?.guest !== false });
      } catch {}
      setRevealedPrice(true);
    } catch (err) {
      setContactError(err?.response?.data?.detail || 'Could not save contact. Please try again.');
    } finally {
      setRevealing(false);
    }
  };

  // Step 2 — open Razorpay. Contact already saved on reveal; re-fire as a
  // safety net in case the user edited the email between reveal and click.
  const handlePurchase = () => {
    const trimmedEmail = (contactEmail || '').trim().toLowerCase();
    const trimmedPhone = (contactPhone || '').trim();
    if (!emailIsValid) {
      setContactError('Please enter a valid email.');
      return;
    }
    setContactError('');
    captureContact({
      device_id: deviceId,
      email: trimmedEmail,
      phone: trimmedPhone || null,
      source: 'iq',
    }).catch(() => { /* non-blocking */ });
    pay({
      deviceId,
      itemType: 'iq_report',
      amount: price,
      currency: currency.currency,
      userName: user?.name,
      userEmail: trimmedEmail,
      userContact: trimmedPhone || undefined,
      allowGuest: true,
      onSuccess: () => setUnlocked(true),
    });
  };

  const handleDownloadPdf = async () => {
    if (!reportRef.current) return;
    setPdfLoading(true);
    try {
      const name = user?.name ? `iq-report-${user.name.split(' ')[0]}` : 'iq-report';
      await downloadElementAsPdf(reportRef.current, name);
      trackEvent('iq_report_download_pdf', { iq_score: iqScore });
    } catch (e) {
      console.error('PDF export failed', e);
    } finally {
      setPdfLoading(false);
    }
  };

  if (!result) return null;

  const { cognitiveScore, iqScore, iqLabel, totalCorrect, totalQuestions, totalTime, avgTime, categoryScores, accuracy } = result;

  // ─── Personalized paywall insights (computed from the user's own results) ───
  // Find the user's strongest category (for teaser-level encouragement)
  const catEntries = Object.entries(categoryScores || {});
  const strongestCat = catEntries.length
    ? catEntries.reduce((a, b) => (b[1] > a[1] ? b : a))
    : null;
  const strongestCatInfo = strongestCat ? categories[strongestCat[0]] : null;

  // IQ → percentile bucket copy (standard normal distribution approximations)
  function percentileBucket(s) {
    if (s >= 130) return { pct: 'top 2%',  label: 'Exceptional',        tone: '#7C3AED' };
    if (s >= 120) return { pct: 'top 10%', label: 'Superior',           tone: '#2563EB' };
    if (s >= 110) return { pct: 'top 25%', label: 'Above Average',      tone: '#0891B2' };
    if (s >=  90) return { pct: 'middle 50%', label: 'Average range',   tone: '#16A34A' };
    if (s >=  80) return { pct: 'bottom 25%', label: 'Below Average',   tone: '#CA8A04' };
    return           { pct: 'bottom 10%', label: 'Developing',          tone: '#DC2626' };
  }
  const bucket = percentileBucket(iqScore);
  const firstDigit = String(iqScore).charAt(0);

  // Speed insight — compared to a 20-question baseline average
  const speedTag = avgTime && avgTime < 12
    ? { text: 'faster than average', tone: '#16A34A' }
    : avgTime && avgTime > 25
    ? { text: 'methodical & careful', tone: '#2563EB' }
    : { text: 'at a steady pace', tone: '#0891B2' };

  // ── Test taken timestamp (for the report header) ──
  const takenAt = result.submitted_at || result.completed_at;
  const takenDate = takenAt ? new Date(takenAt) : null;
  const takenDisplay = takenDate
    ? takenDate.toLocaleString('en-US', {
        weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
        hour: 'numeric', minute: '2-digit', hour12: true,
      })
    : null;

  // ── Per-category personalized descriptions ──
  // Given a category key and the user's % score, pick a tier (strong/solid/growing)
  // and stitch a personalized paragraph using the user's first name + actual % value.
  const firstName = user?.name?.split(' ')[0] || 'there';
  const CAT_COPY = {
    pattern: {
      name: 'Pattern Recognition',
      what: 'the ability to spot sequences, shapes, and underlying rules in visual data. It shows up in strategy, design, coding, and reading social cues.',
      strong: 'you naturally see the hidden logic behind what looks random to most. You\'re wired for strategy, diagnostics, and anticipating what comes next.',
      solid: 'you reliably detect patterns when you slow down and inspect the whole. You can sharpen this further with puzzles, Sudoku, or visual IQ drills.',
      growing: 'patterns sometimes feel elusive under time pressure, which is completely normal. This skill grows fast with daily 5-minute visual puzzles.',
    },
    logic: {
      name: 'Logical Reasoning',
      what: 'the capacity to hold premises in mind and follow them to a valid conclusion. It powers troubleshooting, legal argument, and structured decision-making.',
      strong: 'you think in clean, consequence-aware steps. You spot faulty arguments quickly and can unravel complex scenarios without losing the thread.',
      solid: 'your logic is steady — you arrive at correct answers when you give yourself room to reason. Deduction games and word problems will compound this strength.',
      growing: 'this is your biggest growth lever. Logic improves directly with practice — 10 minutes/day of syllogism or deduction puzzles produces measurable change in 4 weeks.',
    },
    memory: {
      name: 'Short-term Memory',
      what: 'how much information you can hold active and manipulate in the moment — critical for note-taking, multi-step instructions, and learning new material.',
      strong: 'your working memory buffer is large and reliable. You can juggle multi-step processes mentally that others need to write down.',
      solid: 'your recall is dependable for standard tasks. Mnemonic techniques (method-of-loci, chunking) can push this to a high tier surprisingly quickly.',
      growing: 'short-term memory dips under stress for almost everyone. Sleep quality, hydration, and dual N-back training are the three fastest levers.',
    },
    focus: {
      name: 'Focus & Attention',
      what: 'your ability to resist distraction, maintain effort on the right stimulus, and ignore the wrong one. Perhaps the most trainable skill in the whole test.',
      strong: 'you lock in with unusual precision. Timed tests feel like a flow state rather than pressure — a huge advantage in exam settings and deep work.',
      solid: 'your attention is steady, with brief dips that are entirely normal. Protecting it from phones and noise tends to unlock another 10–15% of performance.',
      growing: 'focus is currently scattered — likely a reflection of environment more than ability. Cutting notifications and a 20-minute meditation habit compound very fast here.',
    },
  };

  function tierFor(pct) {
    if (pct >= 70) return 'strong';
    if (pct >= 40) return 'solid';
    return 'growing';
  }

  const categoryBlocks = catEntries.map(([key, pct]) => {
    const meta = categories[key] || {};
    const copy = CAT_COPY[key];
    if (!copy) return null;
    const tier = tierFor(pct);
    const headline =
      tier === 'strong' ? `A clear strength (${pct}%)`
      : tier === 'solid' ? `A solid foundation (${pct}%)`
      : `Your biggest growth area (${pct}%)`;
    const body = copy[tier];
    return { key, pct, color: meta.color || '#879B7D', icon: meta.icon, name: copy.name, what: copy.what, headline, body, tier };
  }).filter(Boolean);

  return (
    <div className="max-w-3xl mx-auto" data-testid="iq-result">
      {authDialog}
      {/* Paywall on top when locked — NO RESULT HINTS.
          Per product direction: don't tease any numbers, categories, percentile,
          strongest skill, accuracy, or pace before purchase. Only say the file
          is ready, that it was scored programmatically, and let them unlock. */}
      {isLocked && (
        <div className="rounded-2xl border mb-8 animate-fade-in overflow-hidden keep-light-theme"
          style={{ background: 'linear-gradient(135deg, #EEF4FF 0%, #F5F0FF 50%, #FDF2F8 100%)', borderColor: '#8B5CF640' }}
          data-testid="iq-paywall">

          {/* Header — celebrate completion (no numeric hints) */}
          <div className="px-6 sm:px-8 pt-8 pb-5 text-center">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full mb-4"
              style={{ background: '#8B5CF615' }}>
              <FileText className="w-7 h-7" style={{ color: '#8B5CF6' }} />
            </div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold mb-3"
              style={{ background: '#8B5CF615', color: '#6B21A8', fontFamily: 'Manrope' }}>
              <CheckCircle2 className="w-3.5 h-3.5" /> Your file is ready
            </div>
            <h3 className="text-2xl sm:text-3xl font-bold mb-3" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>
              Your file is ready
            </h3>
            <p className="text-sm sm:text-[15px] leading-relaxed max-w-md mx-auto" style={{ color: 'var(--muted-fg)' }}>
              Your calculation was done based on your answers using scientific
              programmatic logic. Unlock to download your full PDF report.
            </p>
          </div>

          {/* CTA — two-step: capture contact, then reveal price */}
          <div className="px-6 sm:px-8 pb-7 pt-6 text-center border-t" style={{ borderColor: '#8B5CF620' }}>
            <InlineContactFields
              tone="light"
              email={contactEmail}
              phone={contactPhone}
              onEmailChange={(v) => { setContactEmail(v); if (revealedPrice) setRevealedPrice(false); }}
              onPhoneChange={setContactPhone}
              onEmailBlur={autoCapture.onEmailBlur}
              error={contactError}
            />
            {revealedPrice ? (
              <div className="animate-fade-in" data-testid="iq-price-block">
                <div className="flex items-center justify-center gap-3 mb-4 flex-wrap">
                  <PriceWithPromo inrAmount={price} currency={currency} promo={promo} size="xl" testid="iq-price" />
                  <span className="text-xs px-2 py-1 rounded font-semibold" style={{ background: '#8B5CF615', color: '#8B5CF6' }}>one-time</span>
                </div>
                {!currency.isINR && !currency.loading && (
                  <p className="text-xs mb-3" style={{ color: 'var(--muted-fg)' }}>
                    Billed in {currency.currency} · INR ₹{promo.valid ? Math.round(promo.apply(price)) : price}
                  </p>
                )}
                {payError && <p className="text-xs mb-3" style={{ color: '#DC2626' }}>{payError}</p>}
                <button onClick={handlePurchase} disabled={payLoading} data-testid="buy-iq-btn"
                  className="px-10 py-3.5 rounded-lg font-semibold transition-all hover:opacity-90 active:scale-[0.98] disabled:opacity-60 inline-flex items-center gap-2"
                  style={{ background: 'linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)', color: 'white' }}>
                  {payLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                  {payLoading ? 'Processing...' : `Unlock PDF — ${currency.format(promo.valid ? promo.apply(price) : price)}`}
            </button>
            <p className="text-xs mt-3" style={{ color: 'var(--muted-fg)' }}>
              Or get everything with <span className="font-semibold" style={{ color: 'var(--primary)' }}>Premium</span>
            </p>
              </div>
            ) : (
              <button
                onClick={handleRevealPrice}
                disabled={!emailIsValid || revealing}
                data-testid="iq-reveal-price-btn"
                className="px-10 py-3.5 rounded-lg font-semibold transition-all hover:opacity-90 active:scale-[0.98] disabled:opacity-50 inline-flex items-center gap-2"
                style={{ background: 'linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)', color: 'white' }}>
                {revealing && <Loader2 className="w-4 h-4 animate-spin" />}
                {revealing ? 'Saving…' : 'Unlock My Results'}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Score Cards — hidden entirely when locked (no blurred hints either).
          Only rendered once the user has unlocked so no numeric/category
          details can be inferred from a blurred-but-peekable preview. */}
      {!isLocked && (
      <div ref={reportRef}>
        {/* Report header with taken-at timestamp */}
        {!isLocked && takenDisplay && (
          <div className="rounded-xl border mb-6 px-5 py-4 flex items-center gap-4 flex-wrap"
            style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}
            data-testid="iq-report-header">
            <div className="flex-1 min-w-0">
              <p className="text-xs uppercase tracking-widest font-semibold mb-1"
                style={{ color: 'var(--muted-fg)', fontFamily: 'Manrope' }}>
                {firstName && firstName !== 'there' ? `${firstName}'s cognitive report` : 'Your cognitive report'}
              </p>
              <p className="text-sm" style={{ color: 'var(--text)' }}>
                Test taken on <span className="font-semibold">{takenDisplay}</span>
              </p>
            </div>
          </div>
        )}
        {/* Main Scores */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="rounded-xl p-6 text-center border animate-slide-up"
            style={{ background: 'linear-gradient(135deg, #3B82F610 0%, #3B82F605 100%)', borderColor: '#3B82F630' }}>
            <p className="text-xs uppercase tracking-widest mb-2 font-semibold" style={{ color: '#3B82F6', fontFamily: 'Manrope' }}>Calculated IQ</p>
            <p className="text-5xl font-bold mb-1" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>{iqScore}</p>
            <span className="text-xs px-3 py-1 rounded-full font-medium" style={{ background: '#3B82F615', color: '#3B82F6' }}>{iqLabel}</span>
          </div>
          <div className="rounded-xl p-6 text-center border animate-slide-up"
            style={{ background: 'linear-gradient(135deg, #8B5CF610 0%, #8B5CF605 100%)', borderColor: '#8B5CF630' }}>
            <p className="text-xs uppercase tracking-widest mb-2 font-semibold" style={{ color: '#8B5CF6', fontFamily: 'Manrope' }}>Cognitive Score</p>
            <p className="text-5xl font-bold mb-1" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>{cognitiveScore}</p>
            <span className="text-xs px-3 py-1 rounded-full font-medium" style={{ background: '#8B5CF615', color: '#8B5CF6' }}>out of 100</span>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          <div className="rounded-lg p-4 border text-center" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
            <p className="text-lg font-bold" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>{totalCorrect}/{totalQuestions}</p>
            <p className="text-xs" style={{ color: 'var(--muted-fg)' }}>Correct</p>
          </div>
          <div className="rounded-lg p-4 border text-center" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
            <p className="text-lg font-bold" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>{accuracy}%</p>
            <p className="text-xs" style={{ color: 'var(--muted-fg)' }}>Accuracy</p>
          </div>
          <div className="rounded-lg p-4 border text-center" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
            <p className="text-lg font-bold" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>{avgTime}s</p>
            <p className="text-xs" style={{ color: 'var(--muted-fg)' }}>Avg Time</p>
          </div>
        </div>

        {/* Category Breakdown — personalized descriptions */}
        <div className="rounded-lg p-6 border mb-6" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
          <div className="flex items-center gap-2 mb-2">
            <BarChart3 className="w-5 h-5" style={{ color: '#3B82F6' }} />
            <h3 className="font-bold text-sm" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>Category Breakdown</h3>
          </div>
          <p className="text-xs mb-5" style={{ color: 'var(--muted-fg)' }}>
            What each score says about how your mind works.
          </p>
          <div className="space-y-5">
            {categoryBlocks.map((b) => {
              const toneBg = b.tier === 'strong' ? '#16A34A15' : b.tier === 'solid' ? '#2563EB12' : '#CA8A0415';
              const toneFg = b.tier === 'strong' ? '#16A34A' : b.tier === 'solid' ? '#2563EB' : '#CA8A04';
              return (
                <div key={b.key} className="rounded-lg p-4 border" style={{ background: 'var(--bg)', borderColor: 'var(--border)' }}>
                  {/* Row 1: icon + name + score pill */}
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-xl">{b.icon}</span>
                    <span className="font-bold text-sm flex-1" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>
                      {b.name}
                    </span>
                    <span className="text-xs px-2.5 py-1 rounded-full font-semibold"
                      style={{ background: toneBg, color: toneFg }}>
                      {b.headline}
                    </span>
                  </div>
                  {/* Row 2: progress bar */}
                  <div className="h-2 rounded-full overflow-hidden mb-3" style={{ background: 'var(--muted)' }}>
                    <div className="h-full rounded-full transition-all duration-700"
                      style={{ width: `${b.pct}%`, background: b.color }} />
                  </div>
                  {/* Row 3: what-it-is + personalized description */}
                  <p className="text-xs leading-relaxed mb-2" style={{ color: 'var(--muted-fg)' }}>
                    <span className="font-semibold" style={{ color: 'var(--text)' }}>What it measures:</span> {b.what}
                  </p>
                  <p className="text-[13px] leading-relaxed" style={{ color: 'var(--text)' }}>
                    <span className="font-semibold">Your result:</span> {b.body}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Disclaimer */}
        <p className="text-xs text-center mb-6 px-4 leading-relaxed" style={{ color: 'var(--muted-fg)' }}>
          This report is based on your test performance and is not a clinical IQ measurement. For a professional assessment, consult a licensed psychologist.
        </p>
      </div>
      )}

      {/* Actions */}
      <div className="flex gap-3 mt-4 flex-wrap" data-pdf-ignore="true">
        <button onClick={onRetake} data-testid="retake-iq"
          className="flex-1 min-w-[120px] py-3 rounded-lg font-medium text-sm transition-all hover:opacity-80 flex items-center justify-center gap-2"
          style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text)' }}>
          <RefreshCw className="w-4 h-4" /> Retake
        </button>
        {!isLocked && (
          <button onClick={handleDownloadPdf} disabled={pdfLoading} data-testid="download-iq-pdf"
            className="flex-1 min-w-[160px] py-3 rounded-lg font-medium text-sm transition-all hover:opacity-90 disabled:opacity-60 flex items-center justify-center gap-2"
            style={{ background: 'var(--surface)', border: '1px solid #8B5CF660', color: '#6B21A8' }}>
            {pdfLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            {pdfLoading ? 'Preparing…' : 'Download PDF'}
          </button>
        )}
        <button onClick={() => {
          if (navigator.share) navigator.share({ title: 'My IQ Score', text: `My calculated IQ on the Today Cognitive Test is ${iqScore}.` });
        }} data-testid="share-iq"
          className="flex-1 min-w-[120px] py-3 rounded-lg font-medium text-sm transition-all hover:opacity-90 flex items-center justify-center gap-2"
          style={{ background: '#3B82F6', color: 'white' }}>
          <Share2 className="w-4 h-4" /> Share Score
        </button>
      </div>
    </div>
  );
}
